// Pure parsing and rendering helpers for NumberSeries mask strings.
//
// Mirrors the Ruby model side (NumberSeries#parse_mask_tokens,
// #format_constructor, #post_counter_suffix) so client-rendered preview
// matches what the server would produce. Kept side-effect-free and DOM-free
// so the rules can be exercised in Vitest.

// Splits a mask string into ordered tokens. Mirror of
// NumberSeries#parse_mask_tokens on the Ruby side.
//
// Token shapes:
//   { type: "year",           value: "YY" | "YYYY" }
//   { type: "month" }
//   { type: "counter",        value: "C" * width }
//   { type: "vs_digit_token" }
//   { type: "literal",        value: "FAK" | "-" | ... }
export function tokenizeMask(maskString) {
  if (!maskString) return []
  const rawTokens = maskString.match(/(\{[^}]+\}|[^{}]+)/g) || []
  return rawTokens.map((part) => {
    const inner = part.match(/^\{(.+)\}$/)
    if (inner) {
      const value = inner[1]
      if (/^Y+$/.test(value)) return { type: "year", value }
      if (value === "MM") return { type: "month" }
      if (/^C+$/.test(value)) return { type: "counter", value }
      if (value === "T") return { type: "vs_digit_token" }
      return { type: "literal", value }
    }
    return { type: "literal", value: part }
  })
}

// The period after which the counter resets: "month", "year" or "none" (a
// lifetime total). Mirror of NumberSeries#reset_period — the server compares
// only the rendered prefix BEFORE the counter, so a year or month after the
// counter ({CCCC}-{YY}) is a per-document suffix and resets nothing.
export function resetPeriodFromMask(maskString) {
  const tokens = tokenizeMask(maskString)
  const counterIndex = tokens.findIndex((t) => t.type === "counter")
  const leading = counterIndex === -1 ? tokens : tokens.slice(0, counterIndex)
  if (leading.some((t) => t.type === "month")) return "month"
  if (leading.some((t) => t.type === "year")) return "year"
  return "none"
}

// Flat user-facing render of a mask — counter shows the starting value
// (zero-padded to the token width), `{N}` resolves to `autoDigit`, year/month
// fill from `date`. Drives the rightmost preview input and the VS computation.
//
// opts: { autoDigit (string|number|null|""), date (Date), offset (string|number) }
//   `offset` is the counter value the series starts at (the "Prvé číslo" form
//   field). Default 1. The server applies it to ONE period only (the one the
//   series is first used in), so this preview is exact for a new series.
export function renderPreviewFromMask(maskString, { autoDigit = "", date = new Date(), offset = 1 } = {}) {
  const tokens = tokenizeMask(maskString)
  const { yy, yyyy, mm } = dateParts(date)
  const autoDigitStr = autoDigit == null ? "" : String(autoDigit)

  return tokens
    .map((t) => {
      if (t.type === "year") return t.value === "YY" ? yy : yyyy
      if (t.type === "month") return mm
      if (t.type === "counter") return paddedCounter(t.value.length, offset)
      if (t.type === "vs_digit_token") return autoDigitStr
      return t.value
    })
    .join("")
}

// Returns segment chips for whatever tokens appear AFTER the counter.
// The pre-counter segments come from radio state in the controller; the
// post-counter ones can only be derived from the typed mask itself.
//
// opts: { autoDigit, date, labels: { vs, year, month } }
// Each segment: { kind: "vs" | "year" | "month" | "divider", value, label }
export function postCounterSegments(maskString, { autoDigit = "", date = new Date(), labels = {} } = {}) {
  const tokens = tokenizeMask(maskString)
  const counterIdx = tokens.findIndex((t) => t.type === "counter")
  if (counterIdx === -1) return []

  const { yy, yyyy, mm } = dateParts(date)
  const autoDigitStr = autoDigit == null ? "" : String(autoDigit)

  const out = []
  for (const t of tokens.slice(counterIdx + 1)) {
    if (t.type === "vs_digit_token") {
      if (autoDigitStr) out.push({ kind: "vs", value: autoDigitStr, label: labels.vs })
    } else if (t.type === "year") {
      out.push({ kind: "year", value: t.value === "YY" ? yy : yyyy, label: labels.year })
    } else if (t.type === "month") {
      out.push({ kind: "month", value: mm, label: labels.month })
    } else if (t.type === "literal") {
      // One chip per character so the muted divider styling reads as a
      // separator, not a labeled "prefix"-style block.
      for (const ch of t.value) out.push({ kind: "divider", value: ch, label: "" })
    }
  }
  return out
}

// Returns the full segments array (prefix → year → month → vs → counter → …)
// directly from a mask string — replaces the controller's old radio-driven
// segment builder. Used so exotic masks (year-less, narrow counter widths,
// custom shapes) reflect their actual rendered output instead of whatever
// the radio defaults happened to be.
//
// Pre-counter literals containing a digit are interpreted as legacy VS-digit
// shape (`FAK{YY}1{CCCC}`). Pre-counter literals with no digits render as
// divider chips (one per character so the muted styling reads as a separator).
//
// opts: { autoDigit, date, offset, labels: { prefix, year, month, vs, counter } }
export function segmentsFromMask(maskString, { autoDigit = "", date = new Date(), offset = 1, labels = {} } = {}) {
  const tokens = tokenizeMask(maskString)
  if (tokens.length === 0) return []

  const { yy, yyyy, mm } = dateParts(date)
  const autoDigitStr = autoDigit == null ? "" : String(autoDigit)

  // Prefix = consecutive literals at the very start, concatenated into one chip.
  const firstStructuredIdx = tokens.findIndex((t) => t.type !== "literal")
  const segs = []

  if (firstStructuredIdx > 0) {
    const prefix = tokens
      .slice(0, firstStructuredIdx)
      .map((t) => t.value)
      .join("")
    if (prefix) segs.push({ kind: "prefix", value: prefix, label: labels.prefix })
  }

  // Everything else: render in token order. Mid-mask literals split into
  // per-character chips. A literal digit BEFORE the counter is the legacy
  // VS shape (`FAK{YY}1{CCCC}` — the `1` was the VS); a literal digit AFTER
  // the counter is just static text and renders as a divider. Track
  // `sawCounter` so digits don't get mislabelled VS in the post-counter slot.
  const startIdx = firstStructuredIdx === -1 ? tokens.length : firstStructuredIdx
  let sawCounter = false
  for (let i = startIdx; i < tokens.length; i++) {
    const t = tokens[i]
    if (t.type === "year") {
      segs.push({ kind: "year", value: t.value === "YY" ? yy : yyyy, label: labels.year })
    } else if (t.type === "month") {
      segs.push({ kind: "month", value: mm, label: labels.month })
    } else if (t.type === "counter") {
      segs.push({ kind: "counter", value: paddedCounter(t.value.length, offset), label: labels.counter })
      sawCounter = true
    } else if (t.type === "vs_digit_token") {
      if (autoDigitStr) segs.push({ kind: "vs", value: autoDigitStr, label: labels.vs })
    } else if (t.type === "literal") {
      for (const ch of t.value) {
        if (!sawCounter && /\d/.test(ch)) {
          segs.push({ kind: "vs", value: ch, label: labels.vs })
        } else {
          segs.push({ kind: "divider", value: ch, label: "" })
        }
      }
    }
  }

  return segs
}

// Zero-pad `offset` (the user's starting counter value) to `width` digits.
// "5" + width 4 → "0005"; "12345" + width 4 → "2345" (last N kept).
function paddedCounter(width, offset) {
  const digits = String(offset ?? "1").replace(/\D/g, "") || "1"
  if (digits.length >= width) return digits.slice(-width)
  return "0".repeat(width - digits.length) + digits
}

function dateParts(date) {
  const yyyy = date.getFullYear().toString()
  return {
    yyyy,
    yy: yyyy.slice(-2),
    mm: `0${date.getMonth() + 1}`.slice(-2)
  }
}
